import { useCallback } from 'react'

import { ThumbnailFileFragment } from 'types/graphql'

import { FileUpload } from 'src/components/FileUpload/FileUpload'
import { PathPrefixes } from 'src/constants/file'
import { VStack } from 'src/govwell-ui'
import { T } from 'src/govwell-ui'
import { FileUploadStatuses, useFileUploadState } from 'src/hooks/use-file-upload-state'
import { TanstackField } from 'src/utils/forms'

export type FileUploadFieldProps<TKey extends string> = {
  accept: string
  field: TanstackField<ThumbnailFileFragment | null, TKey>
  isRequired?: boolean
  label?: React.ReactNode
  pathPrefix: PathPrefixes
}

const FileUploadField = <TKey extends string>({
  accept,
  field,
  isRequired,
  label,
  pathPrefix,
}: FileUploadFieldProps<TKey>) => {
  const handleAfterUpload = useCallback(
    async (newFile: ThumbnailFileFragment) => {
      field.setValue(newFile)
    },
    [field]
  )

  const handleAfterRemove = useCallback(async () => {
    field.setValue(null)
  }, [field])

  const fileUploadState = useFileUploadState({
    accept,
    pathPrefix,
    afterUpload: handleAfterUpload,
    afterRemove: handleAfterRemove,
  })

  const currentFile = field.state.value
  const showExisting = currentFile && fileUploadState.status === FileUploadStatuses.Default

  const errorMessage = field.state.meta.errors.join(', ')

  return (
    <VStack gap={2} align="stretch">
      {label && (
        <T.Label>
          {label}
          {isRequired && <span className="text-red-700"> *</span>}
        </T.Label>
      )}
      {showExisting ? (
        // Show existing file preview with option to change
        <FileUpload
          accept={accept}
          {...fileUploadState}
          status={FileUploadStatuses.Existing}
          name={currentFile.name ?? ''}
          url={currentFile.url?.signedUrl}
          thumbnailUrl={currentFile.thumbnailUrl?.signedUrl}
          govWellFileId={currentFile.id}
          size={currentFile.size ?? undefined}
          type={currentFile.type ?? undefined}
          error={!!errorMessage}
        />
      ) : (
        <FileUpload accept={accept} {...fileUploadState} error={!!errorMessage} />
      )}
      {errorMessage && <T.BodySmall className="text-red-700">{errorMessage}</T.BodySmall>}
    </VStack>
  )
}

export default FileUploadField
